import { observable } from 'mobx'

import { z } from '@authy/lib-common/utils/zod'
import { client, RouterInput } from '@authy/lib-client/trpc'
import { ApiLogMethodSchema } from '@authy/lib-common/entities'

import { appState } from '@/appState'

type SortType = NonNullable<RouterInput['api']['apiLogs']['listApiLogs']['sort']>

export const FilterSchema = z.object({
    fromRequestedAt: z.coerce.date().optional(),
    toRequestedAt: z.coerce.date().optional(),
    status: z.enum(['success', 'error', 'all']).nullish(),
    method: z.union([ApiLogMethodSchema, z.literal('all')]).nullish(),
})

type Filter = z.infer<typeof FilterSchema>

export const SearchSchema = FilterSchema.extend({
    sort: z
        .string()
        .transform((val) => val as Extract<keyof Omit<SortType, 'fromRequestedAt' | 'toRequestedAt'>, string>)
        .optional()
        .default('createdAt'),
    sortBy: z.enum(['asc', 'desc']).optional().default('desc'),
    searchTerm: z.string().optional(),
})

export const createDefaultFilter = (filter: Filter): Required<Filter> => ({
    fromRequestedAt: filter.fromRequestedAt || new Date(),
    toRequestedAt: filter.toRequestedAt || new Date(),
    status: filter.status || 'all',
    method: filter.method || 'all',
})

export type Search = z.infer<typeof SearchSchema>

export const pageSize = 50

export const listApiLogs = async (search: Search, offset: number, abortController?: AbortController) => {
    const result = await client.api.apiLogs.listApiLogs.query(
        {
            tenantId: appState.tenant?._id.toString() ?? '',
            searchTerm: search.searchTerm,
            sort: {
                [search.sort]: search.sortBy,
            },
            offset,
            fromRequestedAt: search.fromRequestedAt ? new Date(search.fromRequestedAt) : undefined,
            toRequestedAt: search.toRequestedAt ? new Date(search.toRequestedAt) : undefined,
            status: search.status != 'all' ? search.status : undefined,
            method: search.method != 'all' ? search.method : undefined,
            limit: pageSize,
            returnCount: offset === 0,
        },
        { signal: abortController?.signal }
    )

    return observable(result)
}
